import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiAdmin } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import InvitationUploader from "@/components/InvitationUploader";
import CategoryIcon from "@/components/CategoryIcon";
import { Save, X, Pencil } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");

// Convert ISO string → { date: "YYYY-MM-DD", time: "HH:MM" } in local time
const splitDateTime = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: "", time: "" };
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

const EditEventDialog = ({ event, open, onOpenChange, onSaved }) => {
  const { categories } = useSettings();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!event) return;
    const { date, time } = splitDateTime(event.event_datetime);
    setForm({
      category: event.category || "",
      date,
      time,
      whose_event: event.whose_event || "",
      families: event.families || "",
      venue: event.venue || "",
      address: event.address || "",
      notifier_name: event.notifier_name || "",
      contact_number: event.contact_number || "",
      invitation_url: event.invitation_url || "",
    });
  }, [event]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.category || !form.date || !form.time) {
      toast.error("Kategori, tarih ve saat zorunlu");
      return;
    }
    setSaving(true);
    try {
      const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
      const payload = {
        category: form.category,
        event_datetime: iso,
        whose_event: form.whose_event.trim(),
        families: form.families.trim(),
        venue: form.venue.trim(),
        address: form.address.trim(),
        notifier_name: form.notifier_name.trim(),
        contact_number: form.contact_number.trim(),
        invitation_url: form.invitation_url || null,
      };
      const { data } = await apiAdmin.put(`/events/${event.id}`, payload);
      toast.success("Etkinlik güncellendi");
      onSaved?.(data);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#F9F6F0] border-stone-200 max-h-[92vh] overflow-y-auto"
        data-testid="edit-event-dialog"
      >
        <DialogHeader className="text-left">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Düzenle</div>
          <DialogTitle className="font-display text-2xl text-[#1F2937] flex items-center gap-2">
            <Pencil className="w-5 h-5 text-[#8A252C]" />
            Etkinlik Düzenleme
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Category chips */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">
              Kategori
            </Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {categories.map((c) => {
                const active = form.category === c.value;
                return (
                  <button
                    type="button"
                    key={c.value}
                    onClick={() => set("category", c.value)}
                    data-testid={`edit-cat-${c.value}`}
                    className={`flex items-center gap-2 px-3 h-10 rounded-xl border-2 text-sm font-semibold transition ${
                      active
                        ? "border-[#8A252C] bg-[#8A252C]/5 text-[#8A252C]"
                        : "border-stone-200 bg-white text-[#4B5563] hover:border-[#8A252C]/40"
                    }`}
                  >
                    <span style={{ color: c.accent }}>
                      <CategoryIcon icons={c.icons} />
                    </span>
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Tarih</Label>
              <Input type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)}
                data-testid="edit-date-input"
                className="mt-1 bg-white border-stone-300 h-11" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Saat</Label>
              <Input type="time" value={form.time || ""} onChange={(e) => set("time", e.target.value)}
                data-testid="edit-time-input"
                className="mt-1 bg-white border-stone-300 h-11" />
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Kimin Etkinliği</Label>
            <Input value={form.whose_event || ""} onChange={(e) => set("whose_event", e.target.value)}
              data-testid="edit-whose-input"
              className="mt-1 bg-white border-stone-300 h-11" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Aileleri</Label>
            <Input value={form.families || ""} onChange={(e) => set("families", e.target.value)}
              data-testid="edit-families-input"
              className="mt-1 bg-white border-stone-300 h-11" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Mekan</Label>
            <Input value={form.venue || ""} onChange={(e) => set("venue", e.target.value)}
              data-testid="edit-venue-input"
              className="mt-1 bg-white border-stone-300 h-11" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Adres</Label>
            <Textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)}
              data-testid="edit-address-input"
              className="mt-1 bg-white border-stone-300 resize-none" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Bildiren</Label>
              <Input value={form.notifier_name || ""} onChange={(e) => set("notifier_name", e.target.value)}
                data-testid="edit-notifier-input"
                className="mt-1 bg-white border-stone-300 h-11" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">İletişim</Label>
              <Input value={form.contact_number || ""} onChange={(e) => set("contact_number", e.target.value)}
                data-testid="edit-contact-input"
                className="mt-1 bg-white border-stone-300 h-11" />
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-[#8A252C]/80 font-bold">Davetiye Görseli</Label>
            <div className="mt-1">
              <InvitationUploader
                value={form.invitation_url || ""}
                onChange={(url) => set("invitation_url", url || "")}
                compact
                title="Davetiye görselini değiştirin"
                hint="PNG, JPG, WebP · en fazla 10 MB · opsiyonel"
                doneLabel="Davetiye yüklendi"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="edit-cancel-btn"
            className="border-stone-300 h-11 gap-2">
            <X className="w-4 h-4" /> Vazgeç
          </Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-btn"
            className="bg-[#8A252C] hover:bg-[#6f1d24] text-white h-11 gap-2">
            <Save className="w-4 h-4" />
            {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditEventDialog;
